import React, { useEffect } from 'react';
import { Box, Snackbar, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { dismissKeyboard } from '../utils/dismissKeyboard';

const TOAST_META = {
    success: {
        Icon: CheckCircleRoundedIcon,
        color: '#22c55e',
        bg: '#ecfdf3',
    },
    error: {
        Icon: ErrorRoundedIcon,
        color: '#ef4444',
        bg: '#fff1f2',
    },
    warning: {
        Icon: WarningRoundedIcon,
        color: '#f59e0b',
        bg: '#fff7ed',
    },
    info: {
        Icon: InfoRoundedIcon,
        color: '#8a7000',
        bg: '#fff7c2',
    },
};

export default function AppToast({
    open,
    message,
    severity = 'info',
    autoHideDuration = 3000,
    onClose,
    action = null,
    top = 'calc(env(safe-area-inset-top, 0px) + 48px)',
}) {
    const meta = TOAST_META[severity] || TOAST_META.info;
    const Icon = meta.Icon;

    useEffect(() => {
        if (open) dismissKeyboard();
    }, [open]);

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
                top: `${top} !important`,
                left: '50% !important',
                right: 'auto !important',
                transform: 'translateX(-50%)',
                width: 'min(calc(100vw - 32px), 448px)',
                maxWidth: '448px',
                zIndex: 1800,
            }}
        >
            <Box
                role="alert"
                sx={{
                    width: '100%',
                    minHeight: 56,
                    px: 1.5,
                    py: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #eef0f3',
                    boxShadow: '0 12px 30px rgba(26, 26, 26, 0.14)',
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2.5,
                        flex: '0 0 32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: meta.bg,
                        color: meta.color,
                    }}
                >
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        color: '#111827',
                        fontSize: '0.98rem',
                        fontWeight: 700,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-line',
                        wordBreak: 'keep-all',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {message}
                </Typography>
                {action}
            </Box>
        </Snackbar>
    );
}
